import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { PrimaryButton } from '../components/premium';
import { ApiError } from '../lib/api';
import { getOrCreateAnalyticsSession, trackRefVisit } from '../lib/influencerTracking';

export function SignupPage() {
  const nav = useNavigate();
  const { signup } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [founders, setFounders] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref')?.trim();
      const foundersParam =
        params.get('founders') === '1' ||
        params.get('founders') === 'true' ||
        /^(founders50|peak300|founders)$/i.test(params.get('code') ?? '') ||
        /^(founders50|peak300|founders)$/i.test(params.get('promo') ?? '');
      if (ref) {
        void trackRefVisit(ref, 'signup');
      } else if (foundersParam) {
        setFounders(true);
        getOrCreateAnalyticsSession();
        void trackRefVisit('FOUNDERS', 'signup');
      }
    } catch {
      /* ignore */
    }
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (8 > password.length) {
      setErr('Use at least 8 characters for your password.');
      return;
    }
    setBusy(true);
    try {
      await signup(email, password, displayName.trim() || undefined);
      nav('/onboarding', { replace: true });
    } catch (ex: unknown) {
      if (ex instanceof ApiError) {
        try {
          const j = JSON.parse(ex.body ?? '{}') as { error?: string };
          setErr(j.error ?? 'Could not create account.');
        } catch {
          setErr('Could not create account.');
        }
      } else if (
        ex instanceof TypeError ||
        (ex instanceof Error && /fetch|Failed to fetch|ECONNREFUSED|ECONNRESET|Load failed/i.test(ex.message))
      ) {
        setErr('Unable to reach the server. Check your connection and try again.');
      } else {
        setErr('Could not create account.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-body relative flex min-h-screen flex-col items-center justify-center px-4 pb-[env(safe-area-inset-bottom)] pt-[max(2rem,env(safe-area-inset-top))]">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <BrandLogo variant="hero" />
        </div>
        <h1 className="text-center font-display text-2xl font-bold text-[var(--text-primary)]">
          {founders ? 'Founders signup' : 'Create your account'}
        </h1>
        <p className="mt-2 text-center text-sm text-[var(--text-muted)]">
          {founders
            ? '$7.99/month, 7-day free trial, 50% off your first paid month, then 30% off month 2 if you log 25 days.'
            : "Quick setup — we'll personalize your plan next."}
        </p>
        <form onSubmit={onSubmit} className="premium-card mt-8 space-y-4 !p-6">
          <label className="block text-xs font-medium text-[var(--text-muted)]">
            Preferred name <span className="text-[var(--text-muted)]">(optional)</span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1.5 w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3.5 text-sm text-[var(--text-primary)] outline-none focus:border-sky-500/40"
            />
          </label>
          <label className="block text-xs font-medium text-[var(--text-muted)]">
            Email
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1.5 w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3.5 text-sm text-[var(--text-primary)] outline-none focus:border-sky-500/40"
            />
          </label>
          <label className="block text-xs font-medium text-[var(--text-muted)]">
            Password
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="mt-1.5 w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3.5 text-sm text-[var(--text-primary)] outline-none focus:border-sky-500/40"
            />
          </label>
          {err ? <p className="text-sm text-red-300">{err}</p> : null}
          <PrimaryButton type="submit" fullWidth disabled={busy}>
            {busy ? 'Creating…' : 'Continue'}
          </PrimaryButton>
        </form>
        <p className="mt-6 text-center text-sm text-[var(--text-muted)]">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-accent">
            Log in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
